/**
 * Focus handling for modal dialogs and drawers (brief §7): while `active`, Tab and Shift+Tab cycle inside `ref`,
 * Escape calls `onEscape`, focus moves in if it isn't already there, and when the dialog closes focus returns to
 * what had it before (pass `restore: false` when the component already does that itself).
 */
import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const focusablesIn = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.getClientRects().length > 0 && !el.closest("[inert]"));

export function useFocusTrap(ref: RefObject<HTMLElement | null>, active = true, opts: { onEscape?: () => void; restore?: boolean } = {}) {
  const o = useRef(opts);
  o.current = opts;
  // Remember the opener while rendering: autoFocus inside the dialog moves focus before any effect runs.
  const opener = useRef<Element | null>(null), was = useRef(false);
  if (active && !was.current && typeof document !== "undefined") opener.current = document.activeElement;
  was.current = active;
  useEffect(() => {
    const root = ref.current;
    if (!active || !root) return;
    const prev = opener.current as HTMLElement | null;
    if (!root.contains(document.activeElement)) (focusablesIn(root)[0] ?? (root.tabIndex >= -1 && root.hasAttribute("tabindex") ? root : null))?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && o.current.onEscape) { e.preventDefault(); e.stopPropagation(); o.current.onEscape(); return; }
      if (e.key !== "Tab") return;
      const list = focusablesIn(root);
      if (!list.length) { e.preventDefault(); return; }
      const first = list[0], last = list[list.length - 1], at = document.activeElement;
      if (e.shiftKey && (at === first || at === root || !root.contains(at))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (at === last || !root.contains(at))) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (o.current.restore !== false && prev && prev !== document.body && document.contains(prev)) prev.focus();
    };
  }, [active, ref]);
}
